import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { Salesperson } from "../types/api";
import {
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import AddSalespersonModal from "../components/forms/AddSalespersonModal";
import EditSalespersonModal from "../components/forms/EditSalespersonModal";
import toast from "react-hot-toast";

const EmployeeManagement: React.FC = () => {
  const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(20);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSalesperson, setEditingSalesperson] =
    useState<Salesperson | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingSalesperson, setDeletingSalesperson] =
    useState<Salesperson | null>(null);

  const navigate = useNavigate();

  const loadSalespeople = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit,
        sortBy,
        sortOrder,
        search: searchTerm || undefined,
      };
      const response = await apiService.getSalespeople(params);
      if (response.success) {
        if (response.data && response.data.salespeople) {
          setSalespeople(response.data.salespeople || []);
          setTotalCount(response.data.totalCount || 0);
          setTotalPages(response.data.totalPages || 1);
        } else if (response.data && Array.isArray(response.data)) {
          setSalespeople(response.data);
          setTotalCount(response.data.length);
          setTotalPages(1);
        } else {
          setSalespeople([]);
          setTotalCount(0);
          setTotalPages(1);
        }
      } else {
        setSalespeople([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Failed to load salespeople:", error);
      toast.error("Failed to load salespeople");
      setSalespeople([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, sortBy, sortOrder, searchTerm]);

  useEffect(() => {
    loadSalespeople();
  }, [loadSalespeople]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const handleEdit = (salesperson: Salesperson) => {
    setEditingSalesperson(salesperson);
    setShowEditModal(true);
  };

  const handleDelete = (salesperson: Salesperson) => {
    setDeletingSalesperson(salesperson);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingSalesperson) return;
    try {
      const response = await apiService.deleteSalesperson(
        deletingSalesperson.id,
      );
      if (response.success) {
        toast.success("Employee deleted successfully");
        loadSalespeople();
      } else {
        toast.error(response.message || "Failed to delete employee");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete employee");
    } finally {
      setShowDeleteConfirm(false);
      setDeletingSalesperson(null);
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds || seconds === 0) return "0 min";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getInitials = (firstName?: string, lastName?: string): string => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || "";
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || "";
    return firstInitial + lastInitial || "??";
  };

  const getFullName = (firstName?: string, lastName?: string): string => {
    return `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
  };

  const formatGrade = (grade?: string | number) => {
    if (!grade || isNaN(Number(grade)))
      return { label: "N/A", color: "#6b7280" };
    const n = parseFloat(grade.toString());
    const label = n.toFixed(1);
    if (n >= 8) return { label, color: "#16a34a" };
    if (n >= 6) return { label, color: "#d97706" };
    return { label, color: "#dc2626" };
  };

  const SortIcon: React.FC<{ column: string }> = ({ column }) => {
    const active = sortBy === column;
    return sortOrder === "asc" && active ? (
      <ChevronUpIcon
        style={{ width: 12, height: 12, color: active ? "#111" : "#9ca3af" }}
      />
    ) : (
      <ChevronDownIcon
        style={{ width: 12, height: 12, color: active ? "#111" : "#9ca3af" }}
      />
    );
  };

  const startRecord =
    totalCount === 0 ? 0 : Math.min((currentPage - 1) * limit + 1, totalCount);
  const endRecord =
    totalCount === 0 ? 0 : Math.min(currentPage * limit, totalCount);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600&family=Geist+Mono:wght@400;500&display=swap');

        .em-root {
          font-family: 'Geist', -apple-system, sans-serif;
          color: #111;
          --border: #e5e7eb;
          --muted: #6b7280;
          --accent: #2563eb;
          --radius: 8px;
        }

        .em-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: #111;
          color: #fff;
          border: none;
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          letter-spacing: -0.01em;
          transition: background 0.15s;
        }
        .em-btn-primary:hover { background: #333; }

        .em-btn-ghost {
          display: inline-flex;
          align-items: center;
          padding: 6px 10px;
          background: none;
          color: var(--muted);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          font-size: 0.78rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          transition: all 0.15s;
        }
        .em-btn-ghost:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .em-btn-ghost:disabled { opacity: 0.35; cursor: not-allowed; }

        .em-search-wrap {
          position: relative;
        }
        .em-search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          width: 14px;
          height: 14px;
          color: #6b7280;
          pointer-events: none;
        }
        .em-search-input {
          width: 100%;
          padding: 7px 10px 7px 32px;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          background: #fff;
          outline: none;
          transition: border-color 0.15s;
          box-sizing: border-box;
        }
        .em-search-input:focus { border-color: #111; }
        .em-search-input::placeholder { color: #6b7280; }

        .pagination-bar {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .count-text {
          font-size: 0.8rem;
          color: var(--muted);
        }
        .count-text strong { color: #111; font-weight: 600; }

        .rows-select {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: var(--muted);
        }
        .rows-select select {
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 3px 8px;
          font-size: 0.78rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          outline: none;
          background: #fff;
          cursor: pointer;
        }

        .page-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .page-display {
          font-size: 0.78rem;
          color: #374151;
          padding: 0 12px;
          font-variant-numeric: tabular-nums;
          font-family: 'Geist Mono', monospace;
        }

        .table-wrap {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          overflow: hidden;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.83rem;
          table-layout: fixed;
        }

        td, th {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        thead { border-bottom: 1px solid var(--border); }

        th {
          padding: 10px 16px;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #6b7280;
          white-space: nowrap;
          cursor: pointer;
          user-select: none;
          background: #fafafa;
        }
        th:hover { background: #f3f4f6; color: #374151; }

        .th-inner {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          color: #374151;
          vertical-align: middle;
        }

        tbody tr:last-child td { border-bottom: none; }

        tbody tr {
          cursor: pointer;
          transition: background 0.1s;
        }
        tbody tr:hover td { background: #fafafa; }

        .cell-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 600;
          color: #374151;
          flex-shrink: 0;
          font-family: 'Geist Mono', monospace;
          letter-spacing: 0.02em;
        }

        .cell-name {
          font-weight: 500;
          color: #111;
          font-size: 0.83rem;
        }

        .cell-subtext {
          font-size: 0.72rem;
          color: #6b7280;
          margin-top: 1px;
        }

        .grade-value {
          font-family: 'Geist Mono', monospace;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .date-cell {
          font-family: 'Geist Mono', monospace;
          font-size: 0.77rem;
          color: #6b7280;
        }

        .duration-cell {
          font-family: 'Geist Mono', monospace;
          font-size: 0.77rem;
          color: #374151;
        }
        .duration-sub {
          font-size: 0.7rem;
          color: #6b7280;
          margin-top: 1px;
        }

        .desc-cell {
          font-size: 0.78rem;
          color: #6b7280;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          border: 1px solid transparent;
          background: none;
          cursor: pointer;
          transition: all 0.12s;
          color: #6b7280;
        }
        .action-btn:hover { background: #f3f4f6; border-color: #e5e7eb; color: #111; }
        .action-btn.danger:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #e5e7eb;
          border-top-color: #111;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .empty-state {
          text-align: center;
          padding: 64px 24px;
          color: var(--muted);
          font-size: 0.85rem;
        }

        /* Delete modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 50;
          backdrop-filter: blur(2px);
        }
        .modal-box {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 28px;
          width: 380px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .modal-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #fef2f2;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
        }
        .modal-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: #111;
          text-align: center;
          margin-bottom: 8px;
          letter-spacing: -0.02em;
        }
        .modal-body {
          font-size: 0.8rem;
          color: #6b7280;
          text-align: center;
          line-height: 1.55;
          margin-bottom: 20px;
        }
        .modal-body strong { color: #111; font-weight: 600; }
        .modal-actions {
          display: flex;
          gap: 8px;
          justify-content: center;
        }
        .modal-btn-danger {
          padding: 7px 20px;
          background: #dc2626;
          color: #fff;
          border: none;
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          transition: background 0.15s;
        }
        .modal-btn-danger:hover { background: #b91c1c; }
        .modal-btn-cancel {
          padding: 7px 20px;
          background: #fff;
          color: #374151;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          transition: all 0.15s;
        }
        .modal-btn-cancel:hover { background: #f9fafb; }
      `}</style>

      <div
        className="em-root"
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.4rem",
                fontWeight: 600,
                letterSpacing: "-0.03em",
                color: "#111",
                margin: 0,
              }}
            >
              Employees
            </h1>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: "0.83rem",
                color: "#6b7280",
              }}
            >
              Manage your sales team members
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="em-btn-primary"
          >
            <UserPlusIcon style={{ width: 13, height: 13 }} />
            Add Employee
          </button>
        </div>

        {/* Search + Pagination bar */}
        <div
          className="pagination-bar"
          style={{ gap: "16px", flexWrap: "wrap" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div className="em-search-wrap" style={{ width: 240 }}>
              <MagnifyingGlassIcon className="em-search-icon" />
              <input
                type="text"
                className="em-search-input"
                placeholder="Search employees…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <span className="count-text">
              <strong>
                {startRecord}–{endRecord}
              </strong>{" "}
              of <strong>{totalCount}</strong> employees
            </span>
            <div className="rows-select">
              <span>Show</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          <div className="page-nav">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="em-btn-ghost"
            >
              ←
            </button>
            <span className="page-display">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="em-btn-ghost"
            >
              →
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  {[
                    { key: "firstName", label: "Name", width: "300px" },

                    { key: "call_count", label: "Calls", width: "120px" },
                    { key: "avg_grade", label: "Avg Grade", width: "150px" },
                    {
                      key: "seconds_current_period",
                      label: "This Period",
                      width: "150px",
                    },
                    {
                      key: "employmentDate",
                      label: "Employment Date",
                      width: "180px",
                    },
                    {
                      key: "description",
                      label: "Description",
                      width: undefined,
                    },
                    { key: "actions", label: "", width: "90px" },
                  ].map(({ key, label, width }) => (
                    <th
                      key={key}
                      onClick={
                        key !== "actions" ? () => handleSort(key) : undefined
                      }
                      style={{
                        width,
                        cursor: key === "actions" ? "default" : "pointer",
                      }}
                    >
                      <div className="th-inner">
                        {label}
                        {key !== "actions" && key !== "description" && (
                          <SortIcon column={key} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody
                style={{
                  opacity: loading ? 0.4 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {loading && salespeople.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ padding: 48, textAlign: "center" }}
                    >
                      <div className="loading-spinner" />
                    </td>
                  </tr>
                ) : salespeople.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        {searchTerm
                          ? "No employees match your search"
                          : "No employees found"}
                      </div>
                    </td>
                  </tr>
                ) : (
                  salespeople.map((salesperson) => {
                    const grade = formatGrade(salesperson.avgGrade);
                    return (
                      <tr
                        key={salesperson.id}
                        onClick={() =>
                          navigate(`/salesperson/${salesperson.id}`)
                        }
                      >
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <div className="cell-avatar">
                              {getInitials(
                                salesperson.firstName,
                                salesperson.lastName,
                              )}
                            </div>
                            <div>
                              <div className="cell-name">
                                {getFullName(
                                  salesperson.firstName,
                                  salesperson.lastName,
                                )}
                              </div>
                              {salesperson.dateOfBirth && (
                                <div className="cell-subtext">
                                  b.{" "}
                                  {new Date(
                                    salesperson.dateOfBirth,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="date-cell">
                            {salesperson.employmentDate ? (
                              new Date(
                                salesperson.employmentDate,
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            ) : (
                              <span style={{ color: "#9ca3af" }}>—</span>
                            )}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              fontFamily: "'Geist Mono', monospace",
                              fontSize: "0.83rem",
                              fontWeight: 500,
                              color: "#111",
                            }}
                          >
                            {salesperson.callCount || 0}
                          </span>
                        </td>
                        <td>
                          <span
                            className="grade-value"
                            style={{ color: grade.color }}
                          >
                            {grade.label}
                          </span>
                        </td>
                        <td>
                          <div className="duration-cell">
                            {formatDuration(salesperson.secondsCurrentPeriod)}
                          </div>
                          <div className="duration-sub">
                            {salesperson.monthlyMinuteLimit
                              ? `of ${Math.floor(salesperson.monthlyMinuteLimit / 60).toLocaleString()} min`
                              : "Unlimited"}
                          </div>
                        </td>
                        <td>
                          <span className="desc-cell">
                            {salesperson.description || (
                              <span style={{ color: "#9ca3af" }}>
                                No description
                              </span>
                            )}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <button
                              className="action-btn"
                              title="View"
                              onClick={() =>
                                navigate(`/salesperson/${salesperson.id}`)
                              }
                            >
                              <EyeIcon style={{ width: 14, height: 14 }} />
                            </button>
                            <button
                              className="action-btn"
                              title="Edit"
                              onClick={() => handleEdit(salesperson)}
                            >
                              <PencilIcon style={{ width: 14, height: 14 }} />
                            </button>
                            <button
                              className="action-btn danger"
                              title="Delete"
                              onClick={() => handleDelete(salesperson)}
                            >
                              <TrashIcon style={{ width: 14, height: 14 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddSalespersonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadSalespeople();
        }}
      />

      {editingSalesperson && (
        <EditSalespersonModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingSalesperson(null);
          }}
          salesperson={editingSalesperson}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingSalesperson(null);
            loadSalespeople();
          }}
        />
      )}

      {showDeleteConfirm && deletingSalesperson && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-icon">
              <TrashIcon style={{ width: 18, height: 18, color: "#dc2626" }} />
            </div>
            <div className="modal-title">Delete Employee</div>
            <div className="modal-body">
              Are you sure you want to delete{" "}
              <strong>
                {getFullName(
                  deletingSalesperson.firstName,
                  deletingSalesperson.lastName,
                )}
              </strong>
              ? This action cannot be undone. All associated call analyses will
              be deleted.
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingSalesperson(null);
                }}
                className="modal-btn-cancel"
              >
                Cancel
              </button>
              <button onClick={confirmDelete} className="modal-btn-danger">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeManagement;
